import { useCallback, useEffect } from 'react';
import {
  useReportDefinition,
  useReportFiltersManagement,
  useReportSectionSync,
  useReportExport,
} from '@reports/presentation/hooks';
import { ReportTypes } from '@reports/domain/types';
import {
  ReportBreadcrumb,
  ReportFiltersBar,
  ReportHeader,
  ReportSection,
  ReportTabs,
} from '@reports/presentation/components/base';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import type { ReportSection as ReportSectionType } from '@reports/domain/value-objects';

/**
 * Sales Report Content
 *
 * Main content component for the sales report.
 * Displays breadcrumb, header, filters bar, tabs, and sections.
 * Export toasts are handled automatically by the useReportExport hook.
 * Infrastructure dependencies are managed by composition root.
 */
export const SalesReportContent = () => {
  const { getReportDefinition } = useReportDefinition();
  const salesReport = getReportDefinition(ReportTypes.SALES);

  const { appliedFilters, updateFilters, selectReport } = useReportFiltersManagement({
    autoLoad: true,
    autoSave: true,
  });

  const { activeSectionKey, handleSectionChange } = useReportSectionSync({
    sections: salesReport.sections,
  });

  const { exportReport, isLoading } = useReportExport();

  useEffect(() => {
    selectReport(salesReport);
  }, []);

  const handleFiltersChange = useCallback(
    (filters: BlockDataRequestDto) => {
      updateFilters(filters);
    },
    [updateFilters],
  );

  const handleExport = useCallback(() => {
    exportReport(appliedFilters, salesReport.export);
  }, [appliedFilters, exportReport, salesReport.export]);

  return (
    <div className="w-full p-6 font-sans">
      <ReportBreadcrumb items={[{ label: salesReport.metadata.name }]} />

      <ReportHeader report={salesReport} />

      <ReportFiltersBar
        appliedFilters={appliedFilters}
        isExporting={isLoading}
        onExport={handleExport}
        onFiltersChange={handleFiltersChange}
        reportDefinition={salesReport}
      />

      <ReportTabs
        activeSectionKey={activeSectionKey}
        onSectionChange={handleSectionChange}
        sections={salesReport.sections}
      >
        {(section: ReportSectionType) => <ReportSection request={appliedFilters} section={section} />}
      </ReportTabs>
    </div>
  );
};
